/**
 * Single source of truth for admin route access.
 *
 * NOTE: this is a UX layer only. The authoritative boundary is Postgres RLS
 * (every sensitive table is restricted to admin / super_admin) plus server-side
 * role re-checks inside edge functions. Never rely on this map for security.
 */

export type AdminRole = "super_admin" | "admin" | "content_editor" | "support_agent";

export const ADMIN_ROLES: AdminRole[] = ["super_admin", "admin", "content_editor", "support_agent"];
export const ADMIN_PATH = "/sutradhar";
export const ADMIN_LOGIN_PATH = `${ADMIN_PATH}/access`;

export type AccessMode = "full" | "read";

/** Ordered — the first entry a role can access becomes its landing route. */
export const ADMIN_ROUTE_ACCESS: {
  path: string;
  label: string;
  access: Partial<Record<AdminRole, AccessMode>>;
}[] = [
  { path: `${ADMIN_PATH}/dashboard`, label: "Dashboard", access: { super_admin: "full", admin: "full", support_agent: "read", content_editor: "read" } },
  { path: `${ADMIN_PATH}/users`, label: "User Management", access: { super_admin: "full" } },
  { path: `${ADMIN_PATH}/view-as`, label: "View As", access: { super_admin: "full", admin: "full", support_agent: "full" } },
  { path: `${ADMIN_PATH}/cms`, label: "CMS", access: { super_admin: "full", admin: "full", content_editor: "full" } },
  { path: `${ADMIN_PATH}/customers`, label: "Customers", access: { super_admin: "full", admin: "full", support_agent: "read" } },
  { path: `${ADMIN_PATH}/analytics`, label: "Analytics", access: { super_admin: "full", admin: "full" } },
  { path: `${ADMIN_PATH}/transactions`, label: "Transactions", access: { super_admin: "full", admin: "full", support_agent: "read" } },
  { path: `${ADMIN_PATH}/discounts`, label: "Discounts", access: { super_admin: "full", admin: "full" } },
  { path: `${ADMIN_PATH}/content`, label: "Content", access: { super_admin: "full", admin: "full", content_editor: "full" } },
  { path: `${ADMIN_PATH}/products`, label: "Products", access: { super_admin: "full", admin: "full", content_editor: "read" } },
  { path: `${ADMIN_PATH}/subscriptions`, label: "Subscriptions", access: { super_admin: "full", admin: "full" } },
  { path: `${ADMIN_PATH}/notifications`, label: "Notifications", access: { super_admin: "full", admin: "full" } },
  { path: `${ADMIN_PATH}/settings`, label: "Settings", access: { super_admin: "full" } },
];

export function isAdminRole(role: string): role is AdminRole {
  return (ADMIN_ROLES as string[]).includes(role);
}

function entryFor(pathname: string) {
  return ADMIN_ROUTE_ACCESS.find((r) => pathname.startsWith(r.path));
}

function modeFor(roles: AdminRole[], pathname: string): AccessMode | null {
  const entry = entryFor(pathname);
  if (!entry) return null;
  const modes = roles.map((r) => entry.access[r]).filter(Boolean) as AccessMode[];
  if (modes.length === 0) return null;
  return modes.includes("full") ? "full" : "read";
}

export function canAccess(roles: AdminRole[], pathname: string): boolean {
  return modeFor(roles, pathname) !== null;
}

export function canWrite(roles: AdminRole[], pathname: string): boolean {
  return modeFor(roles, pathname) === "full";
}

/** First route the given roles may open — used for redirects instead of dead pages. */
export function landingRoute(roles: AdminRole[]): string | null {
  const entry = ADMIN_ROUTE_ACCESS.find((r) => roles.some((role) => r.access[role]));
  return entry?.path ?? null;
}
